import React from 'react';
import { Input, Pagination, Dropdown, Checkbox, Button } from '@/components/atoms/index';
import { vars } from '@/style/theme.css';
import { Icon } from '@/icons';
import * as styles from './Table.css';

interface WordbookData {
  id: string;
  wordbookName: string;
  wordCount: number;
  isPublic: boolean;
  registrant: string;
  createdAt: string;
  sequenceNo?: number;
}

interface AcademyVocabularyTableProps {
  wordbookData: WordbookData[];
  onRowClick: (wordbookId: string) => void;
  searchText?: string;
  onSearchChange?: (text: string) => void;
  onSearchSubmit?: (text: string) => void;
  onPublicFilterChange?: (isPublic: boolean | null) => void;
  currentPage?: number;
  totalPages?: number;
  totalElements?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  type: 'wordbook' | 'word_exam_upload' | 'word_exam_select';
  onDelete?: (wordbookId: string) => void;
  selectedIds?: string[];
  onSelectionChange?: (wordbookId: string, checked: boolean) => void;
  pagination?: boolean;
}

const AcademyVocabularyTable: React.FC<AcademyVocabularyTableProps> = ({
  wordbookData,
  onRowClick,
  searchText = '',
  onSearchChange,
  onSearchSubmit,
  onPublicFilterChange,
  currentPage = 1,
  totalPages = 1,
  totalElements = 0,
  pageSize = 10,
  onPageChange, 
  type,
  onDelete,
  selectedIds = [],
  onSelectionChange,
  pagination = true,
}) => {
  const getRowNumber = (index: number, wordbook: WordbookData) => {
    if (typeof wordbook.sequenceNo === 'number') {
      return wordbook.sequenceNo;
    }
    const safePage = Math.max(1, currentPage);
    const safePageSize = Math.max(1, pageSize);
    const calculatedTotal = pagination
      ? (safePage - 1) * safePageSize + wordbookData.length
      : wordbookData.length;
    const totalCount = totalElements && totalElements > 0 ? totalElements : calculatedTotal;
    const sequence = totalCount - ((safePage - 1) * safePageSize + index);
    return sequence > 0 ? sequence : 1;
  };

  const renderPublicStatus = (isPublic: boolean) => {
    return isPublic ? 
      <span className="px-2 py-1 text-xs text-green-800 bg-green-100 rounded-full">공개</span> :
      <span className="px-2 py-1 text-xs text-gray-800 bg-gray-100 rounded-full">비공개</span>
  };

  const publicFilterOptions = [
    { label: '전체', value: 'all' },
    { label: '공개', value: 'public' },
    { label: '비공개', value: 'private' },
  ];

  return (
    <main className='flex flex-col flex-1 w-full'>
      <section className='flex flex-col gap-2 items-start pt-3 w-full lg:flex-row lg:justify-start lg:items-center'>
        <Input
          placeholder='검색어를 입력해주세요.'
          className='max-w-[500px]'
          value={searchText}
          onChange={(e) => onSearchChange?.(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (onSearchSubmit) onSearchSubmit(searchText);
              else onSearchChange?.(searchText);
            }
          }}
          icon={<Icon name="search" size={20} color={vars.color.gray[300]} />}
        />
        {type === 'wordbook' && (
          <Dropdown
            placeholder='공개 범위'
            options={publicFilterOptions}
            className='max-w-[200px]'
            onChange={(value) => {
              if (value === 'all') onPublicFilterChange?.(null);
              else if (value === 'public') onPublicFilterChange?.(true);
              else if (value === 'private') onPublicFilterChange?.(false);
            }}
          />
        )}
      </section>

      <section className='flex flex-col flex-1 justify-between pt-5 w-full'>
        <div>
          {/* 테이블 헤더 */}
          <ul className={styles.academyHeaderList}>
            {type === 'word_exam_select' && (
              <li className={`${styles.headerItem} max-w-[80px] justify-center`}>
                <Checkbox 
                  checked={selectedIds.length === wordbookData.length && wordbookData.length > 0}
                  onChange={(checked) => {
                    if (checked) {
                      wordbookData.forEach(wordbook => onSelectionChange?.(wordbook.id, true));
                    } else {
                      wordbookData.forEach(wordbook => onSelectionChange?.(wordbook.id, false));
                    }
                  }}
                />
              </li>
            )}
            <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
            <li className={`${styles.headerItem} w-full`}>단어장명</li>
            {type !== 'word_exam_select' && (
              <li className={`${styles.headerItem} w-full`}>단어갯수</li>
            )}
            {type === 'wordbook' && (
              <li className={`${styles.headerItem} w-full`}>공개 범위</li>
            )}
            <li className={`${styles.headerItem} w-full`}>등록자</li>
            <li className={`${styles.headerItem} w-full`}>등록일</li>
            {type === 'word_exam_upload' && (
              <li className={`${styles.headerItem} w-full justify-center`}>작업</li>
            )}
          </ul>

          {/* 테이블 바디 */}
          <div>
            {wordbookData.map((wordbook, index) => (
              <div 
                key={wordbook.id}
                className={`${styles.mobileCard} ${styles.hoverRow} cursor-pointer`}
                onClick={() => onRowClick(wordbook.id)}
              >
                <ul className={styles.academyBodyList}>
                  {type === 'word_exam_select' && (
                    <li className={`${styles.academyBodyItem} max-w-[80px] justify-center`}>
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(wordbook.id)}
                          onChange={(checked) => {
                            onSelectionChange?.(wordbook.id, checked);
                          }}
                        />
                      </div>
                    </li>
                  )}
                  <li className={`${styles.academyBodyItem} max-w-[80px] justify-center`}>
                    {getRowNumber(index, wordbook)}
                  </li>
                  <li className={styles.academyBodyItem}>
                    <div className={styles.wordText}>
                      {wordbook.wordbookName}
                    </div>
                  </li>
                  {type !== 'word_exam_select' && (
                    <li className={styles.academyBodyItem}>
                      {wordbook.wordCount}개
                    </li>
                  )}
                  {type === 'wordbook' && (
                    <li className={styles.academyBodyItem}>
                      {renderPublicStatus(wordbook.isPublic)}
                    </li>
                  )}
                  <li className={styles.academyBodyItem}>
                    {wordbook.registrant}
                  </li>
                  <li className={styles.academyBodyItem}>
                    {wordbook.createdAt}
                  </li>
                  {type === 'word_exam_upload' && (
                    <li className={`${styles.academyBodyItem} justify-center`}>
                      <Button
                        intent="danger"
                        size="small"
                        outline
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete?.(wordbook.id);
                        }}
                      >
                        삭제
                      </Button>
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>

          {/* 데이터가 없을 때 */}
          {wordbookData.length === 0 && (
            <div className='py-12 text-center text-gray-500'>
              등록된 단어장이 없습니다.
            </div>
          )}
        </div>
        
        {pagination && (
          <div className='py-5'>
            <Pagination
              total={totalElements}
              pageSize={pageSize}
              current={currentPage}
              onChange={(page) => onPageChange?.(page)}
            />
          </div>
        )}
      </section>
    </main>
  );
};

export default AcademyVocabularyTable;